import deepmerge from 'deepmerge';
import { original, produce, setAutoFreeze } from 'immer';
import { get, isArray, isEmpty, isEqual, isEqualWith, isNil, isObject, omit, omitBy, set, unset } from 'lodash-es';
export function produceWithoutFreeze(state, cb) {
    setAutoFreeze(false);
    const nextState = produce(state, cb);
    setAutoFreeze(true);
    return nextState;
}
/**
 * Merges one JSON patch (`path`, `value`, `updateType`) into an immer draft in place: `undefined`
 * unsets the path, an `append` onto two strings concatenates instead of replacing, and a value that
 * already matches what's there is skipped so the draft's identity doesn't change for a no-op write.
 * Reads the "old" value off `original(draft)` so comparing it doesn't spawn nested immer proxies.
 */
export function applyJsonPatch(draft, path, value, updateType) {
    var _a;
    if (value === undefined) {
        unset(draft, path);
        return;
    }
    const oldValue = get((_a = original(draft)) !== null && _a !== void 0 ? _a : draft, path);
    const nextValue = updateType === 'append' && typeof oldValue === 'string' && typeof value === 'string'
        ? `${oldValue}${value}`
        : value;
    if (isEqual(oldValue, nextValue))
        return;
    set(draft, path, nextValue);
}
export function clone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
export function encodeParams(params) {
    const encodedParams = {};
    Object.keys(params).forEach((key) => {
        // decode uri component and then encoding make sure its not double encoded
        encodedParams[key] = encodeURIComponent(decodeURIComponent(params[key]));
    });
    return encodedParams;
}
export const removeEmptyParams = (obj, { recursive, removeEmptyStrings } = {}) => {
    const newObj = Object.assign({}, obj);
    Object.keys(newObj).forEach((k) => {
        const key = k;
        const value = newObj[key];
        if ((removeEmptyStrings && value === '') || isNil(value)) {
            delete newObj[key];
        }
        else if (typeof value === 'object' && !isArray(value) && recursive) {
            const updatedValue = removeEmptyParams(value, { recursive, removeEmptyStrings });
            if (isEmpty(updatedValue))
                delete newObj[key];
        }
    });
    return newObj;
};
export const removeEmptyParamsAndStrings = (obj, recursive) => {
    return removeEmptyParams(obj, { recursive, removeEmptyStrings: true });
};
export function isObjectLiteral(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
}
export function matchesPropertyPath(propertyPath, pathToMatch) {
    if (pathToMatch === '')
        return true;
    // instead of regex based check, small hack to make sure we match strictly do string match against the path
    return `${propertyPath}.`.startsWith(`${pathToMatch}.`) || `${propertyPath}`.startsWith(`${pathToMatch}[`);
}
export function getParentPath(path, pathDelimeter = '.') {
    const parentPathParts = path.split(pathDelimeter);
    parentPathParts.pop();
    return parentPathParts.join(pathDelimeter);
}
export function deepOmitBy(value, predicate) {
    if (isArray(value)) {
        return value.map((item) => deepOmitBy(item, predicate));
    }
    if (isObject(value)) {
        const cleaned = omitBy(value, predicate);
        return Object.fromEntries(Object.entries(cleaned).map(([key, val]) => [key, deepOmitBy(val, predicate)]));
    }
    return value;
}
export function isObjectEqual(obj1, obj2, pathsToIgnore = []) {
    pathsToIgnore.forEach((path) => {
        if (isObject(obj1))
            obj1 = omit(obj1, path);
        if (isObject(obj2))
            obj2 = omit(obj2, path);
    });
    return isEqualWith(deepOmitBy(obj1, isNil), deepOmitBy(obj2, isNil));
}
/** Keeps `prev`'s reference when `next` is deep-equal, so downstream `Object.is` checks and selectors don't refire. */
export function reuseIfEqual(prev, next) {
    return isEqual(prev, next) ? prev : next;
}
// A payload can carry `__proto__` as an own key; plain assignment would move it to the prototype instead.
const setOwn = (target, key, value) => {
    if (key === '__proto__') {
        Object.defineProperty(target, key, { value, writable: true, enumerable: true, configurable: true });
    }
    else {
        target[key] = value;
    }
};
const isPlainRecord = (value) => {
    if (typeof value !== 'object' || value === null)
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};
/**
 * Rebuilds `next` reusing every subtree of `prev` that is deep-equal, so unchanged branches keep
 * their identity and selectors on them don't refire. Returns `prev` itself when nothing changed.
 * Allocates only from the first changed entry onward, so an identical re-send costs one read pass.
 */
export function reuseUnchanged(prev, next) {
    if (Object.is(prev, next))
        return prev;
    if (typeof next !== 'object' || next === null || typeof prev !== 'object' || prev === null)
        return next;
    if (Array.isArray(next)) {
        if (!Array.isArray(prev))
            return next;
        let merged = null;
        for (let i = 0; i < next.length; i++) {
            const reused = reuseUnchanged(prev[i], next[i]);
            if (merged)
                merged[i] = reused;
            else if (reused !== prev[i]) {
                merged = prev.slice(0, i);
                merged[i] = reused;
            }
        }
        if (merged)
            return merged;
        if (prev.length === next.length)
            return prev;
        return (next.length < prev.length ? prev.slice(0, next.length) : [...prev, ...next.slice(prev.length)]);
    }
    if (!isPlainRecord(next) || !isPlainRecord(prev))
        return next;
    const keys = Object.keys(next);
    let merged = null;
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const reused = reuseUnchanged(prev[key], next[key]);
        if (merged)
            setOwn(merged, key, reused);
        else if (reused !== prev[key] || !(key in prev)) {
            merged = {};
            for (let j = 0; j < i; j++)
                setOwn(merged, keys[j], prev[keys[j]]);
            setOwn(merged, key, reused);
        }
    }
    if (merged)
        return merged;
    if (Object.keys(prev).length === keys.length)
        return prev;
    const trimmed = {};
    for (const key of keys)
        setOwn(trimmed, key, prev[key]);
    return trimmed;
}
export function downloadTextAsFile(filename, text) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
}
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result.split(',')[1]);
            }
            else {
                reject(new Error('Failed to convert blob to base64'));
            }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
export function base64ToAudioUrl(base64, format) {
    return `data:audio/${format};base64,${base64}`;
}
export const mergeObjects = (A, B) => {
    if (!B)
        return A;
    return deepmerge(A, B, {
        arrayMerge: (_A, _B) => _B
    });
};
export const deepFreeze = (obj) => {
    if (Object.isFrozen(obj))
        return obj;
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }
    const typedObj = obj;
    Object.keys(typedObj).forEach((key) => {
        if (typeof typedObj[key] === 'object' && typedObj[key] !== null) {
            deepFreeze(typedObj[key]);
        }
    });
    return Object.freeze(typedObj);
};
export const firstOfSet = (set) => {
    return set.values().next().value;
};
export const isStringifiedEqual = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
};
